import React, { Suspense, useState } from 'react';
import { AddOn, Company, Venue } from '@/types';
import { DataErrorBoundary } from '@features/admin/data';
import { Loader2 } from 'lucide-react';
import { CompanyList } from './CompanyList';
import { CompanyApprovalQueue } from './CompanyApprovalQueue';
import { CompanyEditor } from './CompanyEditor';
import { CompanyTeamPanel } from './CompanyTeamPanel';
import { CompanyActivityPanel } from './CompanyActivityPanel';

interface CompaniesTabProps {
  venues: Venue[];
  addOns: AddOn[];
  canEdit: boolean;
}

export const CompaniesTab: React.FC<CompaniesTabProps> = ({ venues, addOns, canEdit }) => {
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [teamPanelOpen, setTeamPanelOpen] = useState(false);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorCompany, setEditorCompany] = useState<Company | null>(null);

  return (
    <div className="animate-fade-in space-y-8">
      {canEdit && (
        <DataErrorBoundary invalidateOnRetry="companies">
          <Suspense
            fallback={
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <div className="bg-white p-6 rounded-lg border border-orange-100 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading pending companies...
                </div>
              </div>
            }
          >
            <CompanyApprovalQueue
              canEdit={canEdit}
              onEdit={(c) => {
                setEditorCompany(c);
                setEditorOpen(true);
              }}
            />
          </Suspense>
        </DataErrorBoundary>
      )}

      <CompanyList
        venues={venues}
        canEdit={canEdit}
        onViewActivity={(c) => {
          setSelectedCompany(c);
          setActivityPanelOpen(true);
        }}
        onViewTeam={(c) => {
          setSelectedCompany(c);
          setTeamPanelOpen(true);
        }}
        onEdit={(c) => {
          setEditorCompany(c);
          setEditorOpen(true);
        }}
      />

      {teamPanelOpen && selectedCompany && (
        <CompanyTeamPanel
          company={selectedCompany}
          canEdit={canEdit}
          onCompanyUpdated={(updated) => setSelectedCompany(updated)}
          onClose={() => {
            setTeamPanelOpen(false);
            setSelectedCompany(null);
          }}
        />
      )}

      {activityPanelOpen && selectedCompany && (
        <CompanyActivityPanel
          company={selectedCompany}
          addOns={addOns}
          onClose={() => {
            setActivityPanelOpen(false);
            setSelectedCompany(null);
          }}
        />
      )}

      {editorOpen && (
        <CompanyEditor
          company={editorCompany}
          venues={venues}
          onClose={() => {
            setEditorOpen(false);
            setEditorCompany(null);
          }}
          onSaved={() => {
            setEditorOpen(false);
            setEditorCompany(null);
          }}
        />
      )}
    </div>
  );
};
